import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import evidenceService from '../services/evidenceService';
import encryptionService from '../services/encryptionService';
import { useAuth } from '../contexts/AuthContext';

function EvidenceManager() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [description, setDescription] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [encrypted, setEncrypted] = useState(false);
  const [encryptionKey, setEncryptionKey] = useState('');
  const [showEncryptionKey, setShowEncryptionKey] = useState(false);
  const [keyStrength, setKeyStrength] = useState(null);

  const { user, isAuthenticated, canUploadEvidence, getMaxUploadSize, logout, isLoading } = useAuth();
  const navigate = useNavigate();

  // Check authentication and redirect if needed
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/evidence-manager' } }, replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center" style={{ padding: '3rem' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show redirecting message (while navigation happens)
  if (!isAuthenticated) {
    return (
      <div className="page-container">
        <div className="text-center" style={{ padding: '3rem' }}>
          <p style={{ color: '#6b7280' }}>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (!canUploadEvidence()) {
    return (
      <div className="page-container">
        <div className="alert alert-error">
          <h3>⚠ Upload Permission Required</h3>
          <p>Your account does not have permission to upload evidence.</p>
          <p>Please contact your administrator to request upload access.</p>
        </div>
      </div>
    );
  }

  const handleEncryptionKeyChange = (e) => {
    const value = e.target.value;
    console.log('[EvidenceManager] Encryption key changed - Length:', value.length);
    setEncryptionKey(value);

    // Validate key strength
    if (value) {
      const validation = encryptionService.validateKey(value);
      console.log('[EvidenceManager] Key validation result:', validation);
      setKeyStrength(validation);
    } else {
      console.log('[EvidenceManager] Key cleared');
      setKeyStrength(null);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    console.log('[EvidenceManager] File selected:', file ? file.name : 'none');
    console.log('[EvidenceManager] Encryption enabled:', encrypted);
    console.log('[EvidenceManager] Encryption key length:', encryptionKey ? encryptionKey.length : 0);

    if (!file) {
      console.log('[EvidenceManager] No file selected, aborting');
      return;
    }

    // Check file size against user's limit
    const maxSizeMB = getMaxUploadSize();
    const fileSizeMB = file.size / (1024 * 1024);
    console.log('[EvidenceManager] File size:', fileSizeMB.toFixed(2), 'MB | Max allowed:', maxSizeMB, 'MB');

    if (fileSizeMB > maxSizeMB) {
      console.log('[EvidenceManager] File too large, aborting');
      setResult({
        success: false,
        message: `File size (${fileSizeMB.toFixed(2)} MB) exceeds your limit of ${maxSizeMB} MB`
      });
      return;
    }

    // Validate encryption key if encryption is enabled
    if (encrypted) {
      console.log('[EvidenceManager] Validating encryption key...');
      if (!encryptionKey) {
        console.log('[EvidenceManager] No encryption key provided');
        setResult({
          success: false,
          message: 'Please enter an encryption key'
        });
        return;
      }

      const validation = encryptionService.validateKey(encryptionKey);
      console.log('[EvidenceManager] Encryption key validation:', validation);
      if (!validation.isValid) {
        console.log('[EvidenceManager] Encryption key validation FAILED');
        setResult({
          success: false,
          message: validation.error
        });
        return;
      }
      console.log('[EvidenceManager] Encryption key validation PASSED');
    }

    console.log('[EvidenceManager] Starting upload process...');
    setUploading(true);
    setResult(null);

    try {
      const uploadResult = await evidenceService.uploadEvidence(
        file,
        {
          description: description || 'Evidence file',
          caseNumber: caseNumber || 'CASE-' + Date.now(),
          uploadedBy: user.id, // Associate with user
          uploadedByUsername: user.username
        },
        {
          encrypted: encrypted,
          encryptionKey: encrypted ? encryptionKey : null
        }
      );

      console.log('[EvidenceManager] Upload complete:', uploadResult);
      setResult(uploadResult);

      // Clear form on success
      if (uploadResult.success) {
        console.log('[EvidenceManager] Clearing form after successful upload');
        setDescription('');
        setCaseNumber('');
        setEncryptionKey('');
        setKeyStrength(null);
        event.target.value = '';
      }
    } catch (error) {
      console.error('[EvidenceManager] Upload error:', error);
      setResult({
        success: false,
        message: error.message
      });
    } finally {
      console.log('[EvidenceManager] Upload process finished');
      setUploading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="flex justify-between align-center mb-4">
        <div>
          <h1>Upload Evidence</h1>
          <p>Secure evidence verification in seconds</p>
        </div>
        <div className="user-info" style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280' }}>
            Logged in as: <strong>{user.firstName} {user.lastName}</strong>
          </p>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#9ca3af' }}>
            {user.userRole} • Max upload: {getMaxUploadSize()} MB
          </p>
          <button
            onClick={logout}
            style={{
              marginTop: '0.5rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.85rem',
              background: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      <div className="card mt-4">
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Description (Optional)
          </label>
          <input
            type="text"
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the evidence"
            disabled={uploading}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="caseNumber" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Case Number (Optional)
          </label>
          <input
            type="text"
            id="caseNumber"
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            placeholder="e.g., CASE-2026-001"
            disabled={uploading}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '1rem'
            }}
          />
        </div>

        {/* Encryption Section */}
        <div className="form-group" style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px'
        }}>
          <div style={{ marginBottom: encrypted ? '1rem' : '0' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={encrypted}
                onChange={(e) => setEncrypted(e.target.checked)}
                disabled={uploading}
                style={{
                  marginRight: '0.5rem',
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: 500, fontSize: '1rem' }}>
                🔒 Encrypt file for sensitive/confidential evidence
              </span>
            </label>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
              Files are encrypted client-side before upload. You'll need the encryption key to decrypt and view the file.
            </p>
          </div>

          {encrypted && (
            <div style={{ marginTop: '1rem' }}>
              <label htmlFor="encryptionKey" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Encryption Key *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showEncryptionKey ? 'text' : 'password'}
                  id="encryptionKey"
                  value={encryptionKey}
                  onChange={handleEncryptionKeyChange}
                  placeholder="Enter a strong encryption key (min 6 characters)"
                  disabled={uploading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    paddingRight: '3rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowEncryptionKey(!showEncryptionKey)}
                  disabled={uploading}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    fontSize: '1.2rem'
                  }}
                  title={showEncryptionKey ? 'Hide key' : 'Show key'}
                >
                  👁️
                </button>
              </div>
              {keyStrength && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                  {keyStrength.isValid ? (
                    <p style={{ color: keyStrength.color, margin: 0 }}>
                      <strong>Key Strength:</strong> {keyStrength.strengthLabel}
                    </p>
                  ) : (
                    <p style={{ color: '#dc2626', margin: 0 }}>
                      {keyStrength.error}
                    </p>
                  )}
                </div>
              )}
              <p style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: 500 }}>
                ⚠️ IMPORTANT: Save this key securely! You will need it to decrypt and view the file.
              </p>
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="file" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Evidence File
          </label>
          <input
            type="file"
            id="file"
            onChange={handleFileUpload}
            onClick={(e) => {
              console.log('[EvidenceManager] File input clicked');
              console.log('[EvidenceManager] Encrypted:', encrypted);
              console.log('[EvidenceManager] Encryption key:', encryptionKey ? `${encryptionKey.length} chars` : 'empty');
              console.log('[EvidenceManager] Uploading:', uploading);
            }}
            disabled={uploading}
            className="form-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '1rem',
              cursor: uploading ? 'not-allowed' : 'pointer'
            }}
          />
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Maximum file size: {getMaxUploadSize()} MB
          </p>
        </div>

        {uploading && (
          <div className="mt-4">
            <div className="spinner"></div>
            <p className="text-center">Verifying evidence...</p>
          </div>
        )}

        {result && result.success && (
          <>
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: result.encrypted
                ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              borderRadius: '8px',
              boxShadow: result.encrypted
                ? '0 4px 12px rgba(124, 58, 237, 0.2)'
                : '0 4px 12px rgba(5, 150, 105, 0.2)'
            }}>
              <h2 style={{
                margin: 0,
                color: 'white',
                fontSize: '1.75rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '2rem' }}>✓</span>
                {result.encrypted ? '🔒 Evidence Encrypted, Verified and Recorded' : 'Evidence Verified and Recorded'}
              </h2>
            </div>

            <div style={{
              marginTop: '1.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem'
            }}>
              {/* Verification Details Column */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.25rem',
                  color: '#1f2937',
                  fontWeight: 600,
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  Verification Details
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      Verification Code
                    </label>
                    <div style={{
                      fontFamily: 'monospace',
                      background: '#f3f4f6',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      color: '#1f2937',
                      fontWeight: 600,
                      border: '1px solid #d1d5db'
                    }}>
                      {result.verificationCode}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      File Name
                    </label>
                    <div style={{ fontSize: '0.95rem', color: '#1f2937', fontWeight: 500 }}>
                      {result.fileName}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      File Hash (SHA-256)
                    </label>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: '#4b5563',
                      wordBreak: 'break-all',
                      background: '#f9fafb',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #e5e7eb'
                    }}>
                      {result.fileHash}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 500 }}>
                      Uploaded At
                    </label>
                    <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
                      {new Date(result.uploadedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Storage Locations Column */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.25rem',
                  color: '#1f2937',
                  fontWeight: 600,
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  Storage Locations
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {result.storage?.ipfs?.cid && (
                    <div style={{
                      padding: '1rem',
                      background: '#f0fdf4',
                      border: '2px solid #86efac',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#059669',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>📦</span>
                        IPFS Storage
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#047857', marginBottom: '0.25rem', fontWeight: 500 }}>
                          CID
                        </label>
                        <div style={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#065f46',
                          wordBreak: 'break-all',
                          background: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          {result.storage.ipfs.cid}
                        </div>
                      </div>
                      {result.storage.ipfs.gatewayUrl && (
                        <div>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: '#047857', marginBottom: '0.25rem', fontWeight: 500 }}>
                            Gateway URL
                          </label>
                          <a
                            href={result.storage.ipfs.gatewayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'block',
                              fontSize: '0.85rem',
                              color: '#059669',
                              wordBreak: 'break-all',
                              textDecoration: 'underline',
                              background: 'white',
                              padding: '0.5rem',
                              borderRadius: '4px'
                            }}
                          >
                            {result.storage.ipfs.gatewayUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {result.storage?.blob?.url && (
                    <div style={{
                      padding: '1rem',
                      background: '#eff6ff',
                      border: '2px solid #93c5fd',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#2563eb',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>☁️</span>
                        Vercel Blob Storage
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#1e40af', marginBottom: '0.25rem', fontWeight: 500 }}>
                          URL
                        </label>
                        <a
                          href={result.storage.blob.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            fontSize: '0.85rem',
                            color: '#2563eb',
                            wordBreak: 'break-all',
                            textDecoration: 'underline',
                            background: 'white',
                            padding: '0.5rem',
                            borderRadius: '4px'
                          }}
                        >
                          {result.storage.blob.url}
                        </a>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#1e40af', marginBottom: '0.25rem', fontWeight: 500 }}>
                          Hash
                        </label>
                        <div style={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: '#1e3a8a',
                          wordBreak: 'break-all',
                          background: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          {result.storage.blob.hash}
                        </div>
                      </div>
                    </div>
                  )}

                  {result.storage?.database?.id && (
                    <div style={{
                      padding: '1rem',
                      background: '#fef3c7',
                      border: '2px solid #fbbf24',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#d97706',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>🗄️</span>
                        Database Record
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#92400e', marginBottom: '0.25rem', fontWeight: 500 }}>
                          Record ID
                        </label>
                        <div style={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: '#78350f',
                          background: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          {result.storage.database.id}
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#92400e', marginBottom: '0.25rem', fontWeight: 500 }}>
                          Table
                        </label>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#78350f',
                          background: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px'
                        }}>
                          {result.storage.database.table}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {result.encrypted && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#fef3c7',
                border: '2px solid #fbbf24',
                borderRadius: '6px',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'start'
              }}>
                <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>⚠️</span>
                <p style={{ margin: 0, color: '#92400e', fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <strong>IMPORTANT:</strong> This file was encrypted. You will need your encryption key to decrypt and view it. Save your encryption key securely - it cannot be recovered if lost.
                </p>
              </div>
            )}

            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#f0fdf4',
              border: '2px solid #86efac',
              borderRadius: '6px',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '1.5rem', flexShrink: 0, color: '#059669' }}>✓</span>
              <p style={{ margin: 0, color: '#047857', fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.6 }}>
                Evidence has been securely recorded and associated with your account.
              </p>
            </div>
          </>
        )}

        {result && !result.success && (
          <div className="alert alert-error mt-4">
            <h3>⚠ Upload Failed</h3>
            <p>{result.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default EvidenceManager;
